import { copyBiTable } from '@euler/app/components/message/widgets/bi-visualization/CopyTableButton';
import { biCardSubtitle } from '@euler/app/components/message/widgets/bi-visualization/functions/biCardSubtitle';
import { biVizIcon } from '@euler/app/components/message/widgets/bi-visualization/functions/biVizIcon';
import { biTableOfViz } from '@euler/app/components/message/widgets/bi-visualization/functions/biVizTable';
import { useBiViewMode } from '@euler/app/components/message/widgets/bi-visualization/functions/useBiViewMode';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  BiCardView,
  BiDashboardFilters,
  BiDateFilterUse,
} from '@euler/model/bi/dashboard';
import { BiQueryResult } from '@euler/model/bi/result';
import { BiPalette, BiTitleAlign } from '@euler/model/bi/viz';
import { CalendarX, Maximize2 } from 'lucide-react-native';
import {
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { View } from 'react-native';
import { BiButton } from '../../components/ui/BiButton';
import { BiTag } from '../../components/ui/BiTag';
import { BiCardKind, biCardKind } from '../../functions/biCardKind';
import { biRowsHeight, kBiCardKindRows } from '../../functions/biGridLayout';
import { BiCardDataView, useBiCardData } from '../functions/useBiCardData';
import { useBiSeen } from '../viewport/BiViewport';
import { CardBody } from './CardBody';
import { CardFrame } from './CardFrame';
import { BiCardMoves } from './CardMenu';
import { CardMenuButton } from './CardMenuButton';

/** A dashboard card that loads its own data. */
export const BiDashboardCard = memo(
  ({
    card,
    height,
    filters,
    savedFilters,
    refreshTick,
    palette,
    grip,
    footer,
    onOpen,
    onEdit,
    moves,
    onRemove,
    onFitHeight,
    onData,
  }: {
    card: BiCardView;
    /** Undefined: as tall as its content (a KPI group fits its tiles). */
    height?: number;
    filters: BiDashboardFilters | undefined;
    savedFilters: BiDashboardFilters;
    /** Bumped when the dashboard is refreshed. */
    refreshTick?: number;
    palette: BiPalette;
    /** The drag handle, on the editable desktop grid only. */
    grip?: ReactNode;
    /** Under the body inside the card: the phone resize grabber. */
    footer?: ReactNode;
    /** Opens the card's own page (title, ⋯ 查看详情); any viewer may. */
    onOpen?: () => void;
    /**
     * Editors only; every action is in the ⋯ menu. Given the result the
     * card shows (none when it failed), so the form starts from it.
     */
    onEdit?: (loaded: BiQueryResult | null) => void;
    /** Editors, where the card cannot be dragged. */
    moves?: BiCardMoves;
    onRemove?: () => void;
    /** Without a height: the height its content needs (`CardFrame`). */
    onFitHeight?: (px: number) => void;
    /** Each change of the card's data, for those that reuse it. */
    onData?: (cardId: string, state: BiCardDataView) => void;
  }) => {
    const frame = useRef<View>(null);
    const { seen, onLayout } = useBiSeen(frame);
    const { state, retry } = useBiCardData(
      card.id,
      card.updatedAt,
      filters,
      savedFilters,
      { enabled: seen, refreshTick },
    );
    useEffect(() => {
      onData?.(card.id, state);
    }, [onData, card.id, state]);
    const { rpx } = useResponsiveContext();
    const ready = state.kind === 'ready' ? state : null;
    // The data behind the card, as its result table shows it, for 复制数据.
    const table = useMemo(
      () =>
        ready?.result.current.rows.length
          ? biTableOfViz(card.viz, ready.result.current)
          : null,
      [ready, card.viz],
    );
    const copy = useCallback(() => {
      if (table) copyBiTable(table);
    }, [table]);
    const { view, toggle } = useBiViewMode(
      card.viz,
      ready?.result.current ?? null,
    );
    // Shorter than the client's own KPI height, e.g. a KPI the AI placed
    // `small`: the chrome tightens so the number still fits.
    const compact =
      height !== undefined &&
      height < biRowsHeight(kBiCardKindRows[BiCardKind.Kpi]);
    return (
      // Measured to find out whether the card is on screen yet. It fills a
      // slot taller than its content: a KPI group grown to its row's height.
      <View
        ref={frame}
        collapsable={false}
        onLayout={onLayout}
        style={{ flexGrow: 1 }}
      >
        <CardFrame
          icon={biVizIcon(card.viz.type)}
          title={card.title}
          onTitlePress={onOpen}
          titleHidden={card.viz.style?.title?.show === false}
          titleCentered={card.viz.style?.title?.align === BiTitleAlign.Center}
          subtitle={
            ready ? biCardSubtitle(card.viz, ready.result.current) : null
          }
          badge={
            ready?.dateFilter === BiDateFilterUse.Ignored ? (
              <BiTag
                text="未应用日期筛选"
                color={aiColors.amber}
                icon={CalendarX}
              />
            ) : null
          }
          trailing={
            toggle || grip || onOpen ? (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: rpx(6),
                }}
              >
                {toggle}
                {onOpen ? (
                  <BiButton
                    icon={Maximize2}
                    tone="quiet"
                    accessibilityLabel={`查看详情 ${card.title}`}
                    onPress={onOpen}
                  />
                ) : null}
                {onOpen ? (
                  <CardMenuButton
                    title={card.title}
                    onRefresh={retry}
                    onCopy={table ? copy : undefined}
                    onOpen={onOpen}
                    onEdit={
                      onEdit
                        ? () => onEdit(ready?.result.current ?? null)
                        : undefined
                    }
                    // The form starts from the card's data: not before it
                    // has loaded, and never a query of its own for it.
                    editDisabled={state.kind === 'loading'}
                    moves={moves}
                    onRemove={onRemove}
                  />
                ) : null}
                {grip}
              </View>
            ) : null
          }
          height={height}
          compact={compact}
          footer={footer}
          onFitHeight={onFitHeight}
        >
          <CardBody
            state={state}
            spec={card.spec}
            viz={card.viz}
            kind={biCardKind(card.viz)}
            compact={compact}
            view={view}
            palette={palette}
            scroll={height !== undefined}
            onRetry={retry}
          />
        </CardFrame>
      </View>
    );
  },
);
